import type { ForecastPreviewResponseDto } from '@/src/api/types/procurement'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import KPIBoxInfo from '@/src/features/procurement-and-markets/load-planning/components/KPIBoxInfo'
import type { LoadPlanningAdjustmentFormValidated } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import { useLoadPlanningDataCurvePreview } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/hooks/useLoadPlanningPreview.hook'
import { isNullOrUndefined } from '@/src/shared/utils'
import { FlexRow } from '@components'
import { useTranslation } from 'react-i18next'

interface LoadPlanningPreviewKpiBoxesProps {
  dataToPreview?: LoadPlanningAdjustmentFormValidated
}

const caluculateKpis = (dataCurve?: ForecastPreviewResponseDto) => {
  const aggregatedDraftConsumption = dataCurve?.kpis?.aggregatedDraftConsumption?.energy?.amount ?? null
  const previewConsumption = dataCurve?.kpis?.aggregatedPreviewConsumption?.energy?.amount ?? null

  if (isNullOrUndefined(aggregatedDraftConsumption) || isNullOrUndefined(previewConsumption)) {
    return null
  }

  const previewDiffPercentage = (previewConsumption / aggregatedDraftConsumption) * 100

  const difference = previewConsumption - aggregatedDraftConsumption

  return {
    aggregatedDraftConsumption,
    previewConsumption,
    previewDiffPercentage,
    difference,
  }
}

export const LoadPlanningPreviewKpiBoxes = ({ dataToPreview }: LoadPlanningPreviewKpiBoxesProps) => {
  const { t } = useTranslation()

  const { data: dataCurve } = useLoadPlanningDataCurvePreview(dataToPreview, GranularityEnum.ONE_MONTH)

  const kpis = caluculateKpis(dataCurve)

  return (
    <FlexRow gap={4}>
      <KPIBoxInfo
        infoText={t('components.procurement.forecasting.graph.tooltip.actual_consumption')}
        kpiValue={kpis?.aggregatedDraftConsumption}
        hidePercentage
      />
      <KPIBoxInfo
        infoText={t('components.procurement.forecasting.graph.tooltip.adjusted_consumption')}
        kpiValue={kpis?.previewConsumption}
        hidePercentage
      />
      <KPIBoxInfo infoText={t('components.procurement.forecasting.graph.tooltip.difference')} kpiValue={kpis?.difference} hidePercentage />
    </FlexRow>
  )
}
